import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { Container } from '@mui/system';
import { FooterContainer } from '../../components/ScreenContainer/FooterScreenContainer/FooterContainer';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { MetaTags } from '../../components/Metatags/Metatags';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GavelIcon from '@mui/icons-material/Gavel';

const STEPS = [
    'Log into your Thspian account.',
    'Go to Settings.',
    "Click 'Delete Account'.",
    'Confirm your deletion request.',
];

const DATA_DELETED = [
    'Profile information',
    'Uploaded media and portfolio content',
    'Campaign submissions',
    'Messages and activity history',
    'Saved preferences',
];

const DATA_RETAINED = [
    'Transaction records for legal and tax compliance',
    'Fraud prevention and security logs',
    'Data required by applicable laws',
];

const DeleteAccount = () => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    return (
        <MetaTags
            title="Thspian | Delete Your Account"
            description="Learn how to delete your Thspian account and understand what data is removed or retained. Request account deletion directly or via email.">
            <Container
                disableGutters
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
                maxWidth={false}>
                <FooterContainer>
                    <Stack spacing={5} sx={{ width: '100%', py: 2 }}>
                        {/* Hero */}
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 28, sm: 36 },
                                    color: t.primary_white,
                                    mb: 1,
                                }}>
                                Delete Your Thspian Account
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: { xs: 14, sm: 16 },
                                    color: t.gray_sidebar,
                                }}>
                                We believe creators should have full control over their data.
                            </Typography>
                        </Box>

                        {/* How to Delete */}
                        <Section
                            icon={<DeleteOutlineIcon sx={{ fontSize: 22, color: t.primary_orange }} />}
                            title="How to Delete Your Account"
                            themeStyle={themeStyle}>
                            <Stack spacing={1.5}>
                                {STEPS.map((step, i) => (
                                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                color: t.primary_orange,
                                                minWidth: 20,
                                            }}>
                                            {i + 1}.
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 14,
                                                color: t.tag_white,
                                            }}>
                                            {step}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Section>

                        {/* Alternative Method */}
                        <Section
                            icon={<EmailOutlinedIcon sx={{ fontSize: 22, color: t.primary_orange }} />}
                            title="Can't Access Your Account?"
                            themeStyle={themeStyle}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: t.tag_white,
                                    mb: 1,
                                }}>
                                You can request deletion by emailing{' '}
                                <Typography
                                    component="a"
                                    href="mailto:support@thspian.com"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 14,
                                        color: t.primary_orange,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}>
                                    support@thspian.com
                                </Typography>{' '}
                                from your registered email address. Include your username in the request.
                            </Typography>
                        </Section>

                        {/* Data Deleted */}
                        <Section
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: 22, color: t.switchGreen }} />}
                            title="What Data Is Deleted"
                            themeStyle={themeStyle}>
                            <Stack spacing={1}>
                                {DATA_DELETED.map((item, i) => (
                                    <BulletItem key={i} text={item} color={t.tag_white} />
                                ))}
                            </Stack>
                        </Section>

                        {/* Data Retained */}
                        <Section
                            icon={<InfoOutlinedIcon sx={{ fontSize: 22, color: t.primary_orange }} />}
                            title="What Data May Be Retained"
                            themeStyle={themeStyle}>
                            <Stack spacing={1}>
                                {DATA_RETAINED.map((item, i) => (
                                    <BulletItem key={i} text={item} color={t.tag_white} />
                                ))}
                            </Stack>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: t.gray_sidebar,
                                    mt: 2,
                                    fontStyle: 'italic',
                                }}>
                                Retention period: Up to 7 years where legally required.
                            </Typography>
                        </Section>

                        {/* Deletion Timeline */}
                        <Section
                            icon={<ScheduleIcon sx={{ fontSize: 22, color: t.primary_orange }} />}
                            title="Deletion Timeline"
                            themeStyle={themeStyle}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: t.tag_white,
                                }}>
                                Accounts and associated personal data are permanently deleted within 30 days after
                                verification.
                            </Typography>
                        </Section>

                        {/* Compliance */}
                        <Section
                            icon={<GavelIcon sx={{ fontSize: 22, color: t.primary_orange }} />}
                            title="Compliance"
                            themeStyle={themeStyle}>
                            <Stack spacing={1}>
                                <BulletItem text="Google Play Data Safety Policy" color={t.tag_white} />
                                <BulletItem text="GDPR where applicable" color={t.tag_white} />
                                <BulletItem text="CCPA where applicable" color={t.tag_white} />
                            </Stack>
                        </Section>
                    </Stack>
                </FooterContainer>
            </Container>
        </MetaTags>
    );
};

const Section: React.FC<{
    icon: React.ReactNode;
    title: string;
    themeStyle: 'light' | 'dark';
    children: React.ReactNode;
}> = ({ icon, title, themeStyle, children }) => {
    const t = theme[themeStyle];
    return (
        <Box
            sx={{
                bgcolor: t.card_grey,
                borderRadius: 3,
                p: { xs: 2.5, sm: 3 },
                border: `1px solid ${t.darkGray60}`,
            }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                {icon}
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 16, sm: 18 },
                        color: t.primary_white,
                    }}>
                    {title}
                </Typography>
            </Stack>
            {children}
        </Box>
    );
};

const BulletItem: React.FC<{ text: string; color: string }> = ({ text, color }) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0,
                opacity: 0.5,
            }}
        />
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                fontSize: 14,
                color,
            }}>
            {text}
        </Typography>
    </Stack>
);

export { DeleteAccount };
